import { capitalize } from '@buyerflo/core/helperFunctions/string'
import {
    getClientNames,
    InternalPurchaserSchema,
    purchaserEnabledForUser,
} from '@buyerflo/core/schemas/purchaser'
import { StatusBadge } from '@buyerflo/ui/components/StatusBadge'
import { useMutation, useQuery } from '@tanstack/react-query'
import { usePurchaserFilesQuery, usePurchasersApi, useUsersApi } from 'apiProviders'
import { useUserData } from 'auth'
import Button from 'components/button'
import { DeleteRounded, EditRounded, MoreVertRounded, SaveRounded } from 'components/icons'
import Loading from 'components/Loading'
import Menu from 'components/menu'
import { ScrollableTabList, ScrollableTabs } from 'components/scrollableTabs'
import SectionHead from 'components/sectionHead'
import { useOrgId } from 'helpers'
import { pick } from 'ramda'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, Route, Routes, useMatch, useNavigate, useParams } from 'react-router'
import { Dialog, Group, SizableText, Spinner, Tabs, Text, View, XStack } from 'tamagui'
import { useTRPC } from 'utils/trpc'
import AuditLog from './auditLog'
import CorePurchaserDetails from './corePurchaserDetails'
import { EditModeProvider, useEditMode } from './editMode'
import Files from './files'
import { usePurchaserStatusOptions } from './helpers'
import PurchasedProperty from './purchasedProperty'
import { PurchaserContext, usePurchaserContext } from './purchaserProvider'
import WishlistView from './wishlistView'

const PurchaserStatusChip = () => {
    const trpc = useTRPC()
    const { purchaser, setPurchaser } = usePurchaserContext()
    const updatePurchaserStatusMutation = useMutation(trpc.purchaser.updatePurchaserStatus.mutationOptions())
    const purchaserStatusOptions = usePurchaserStatusOptions(purchaser)
    const purchasersApi = usePurchasersApi()

    const [loading, setLoading] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)

    if (loading) {
        return <Spinner size="small" />
    }

    return (
        <View>
            <Menu
                open={menuOpen}
                onOpenChange={setMenuOpen}
            >
                <Menu.Trigger asChild>
                    <View
                        cursor="pointer"
                        hoverStyle={{ opacity: 0.85 }}
                        onPress={(e: any) => e.stopPropagation()}
                    >
                        <StatusBadge status={purchaser.purchaserStatus} />
                    </View>
                </Menu.Trigger>
                <Menu.Portal>
                    <Menu.Content>
                        {purchaserStatusOptions.map(({ value, disabled }) => (
                            <Menu.Item
                                key={value}
                                disabled={disabled}
                                onPress={async (e: any) => {
                                    e.stopPropagation()
                                    if (disabled) return
                                    setMenuOpen(false)
                                    setLoading(true)
                                    try {
                                        const updatedPurchaser =
                                            await updatePurchaserStatusMutation.mutateAsync({
                                                purchaserId: purchaser.purchaserId,
                                                purchaserStatus: value,
                                            })

                                        setPurchaser({
                                            ...purchaser,
                                            ...pick(['purchaserStatus', 'balanceSheet'], updatedPurchaser),
                                        })
                                        //setPurchaser({ ...purchaser, purchaserStatus: value })
                                        purchasersApi.refresh()
                                    } catch (e) {
                                        console.error(e)
                                    } finally {
                                        setLoading(false)
                                    }
                                }}
                            >
                                <Menu.ItemTitle>
                                    {value.split(' ').map(capitalize).join(' ')}
                                </Menu.ItemTitle>
                            </Menu.Item>
                        ))}
                    </Menu.Content>
                </Menu.Portal>
            </Menu>
        </View>
    )
}

const Inner = () => {
    const trpc = useTRPC()
    const navigate = useNavigate()
    const { purchaser } = usePurchaserContext()
    const match = useMatch(`:orgId/clients/:purchaserId/:tab`)
    const { user } = useUserData()
    const deletePurchaserMutation = useMutation(trpc.purchaser.deletePurchaser.mutationOptions())
    const { purchaserId } = useParams<{ purchaserId: string }>()
    const formRef = useRef<any>(null)
    const usersApi = useUsersApi()
    const orgId = useOrgId()
    const { editMode, setEditMode } = useEditMode()

    const isNew = purchaserId == 'new'

    const filesQuery = usePurchaserFilesQuery(purchaserId ?? '')
    const filesCount = isNew ? undefined : filesQuery.data?.filter(f => !f.isFolder).length
    const tabCount: Record<string, number | undefined> = { Files: filesCount }

    const tabs = useMemo(
        () =>
            [
                ['Client', CorePurchaserDetails],
                ['Wishlist', WishlistView],
                ['Purchased Property', PurchasedProperty],
                ['Files', Files],
                /*['eSign Documents', () => <GenericDocumentStepper title="Generic Documents" />],*/
                ['Audit Log', AuditLog],
            ] as [string, () => React.ReactNode][],
        [usersApi.isLoading],
    )

    const [showDeletePurchaser, setShowDeletePurchaser] = useState(false)
    const [voidModalOpen, setVoidModalOpen] = useState(false)
    const [saving, setSaving] = useState(false)
    const savingRef = useRef(false)

    const activeTab = match?.params.tab

    const enabledForUser = purchaserEnabledForUser(purchaser, usersApi.data, user as any)
    const formFullyDisabled =
        !isNew &&
        !user.permissions.includes('purchasers.fullControl') &&
        !(enabledForUser && user.permissions.includes('purchasers.edit'))

    // Scroll to active tab
    const tabListRef = useRef<HTMLDivElement>(null)
    const activeTabIndex = tabs.findIndex(([label]) => label.toLowerCase().replaceAll(' ', '-') == activeTab)
    useEffect(() => {
        if (!tabListRef.current || activeTabIndex < 0) return
        const tab = tabListRef.current.querySelector(`[role="tab"]:nth-child(${activeTabIndex + 1})`)
        if (!tab) return
        const tabListRect = tabListRef.current.getBoundingClientRect()
        const tabRect = (tab as HTMLElement).getBoundingClientRect()
        const offset = tabRect.left - tabListRect.left + tabListRef.current.scrollLeft
        const center = offset - tabListRect.width / 2 + tabRect.width / 2
        tabListRef.current.scrollTo({ left: center, behavior: 'smooth' })
    }, [activeTab])

    const [menuOpen, setMenuOpen] = useState(false)
    const anchorRef = useRef<any>(null)

    const handleMenuToggle = () => {
        setMenuOpen(prevOpen => !prevOpen)
    }

    const handleMenuClose = () => {
        setMenuOpen(false)
    }

    // Handle clicks outside the menu
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const menuElement = document.getElementById('purchaser-actions-menu')
            if (
                menuOpen &&
                anchorRef.current &&
                !anchorRef.current.contains(event.target as Node) &&
                menuElement &&
                !menuElement.contains(event.target as Node)
            ) {
                handleMenuClose()
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [menuOpen])

    return (
        <>
            <View
                width="100%"
                height="100%"
                flex={1}
                display="flex"
                flexDirection="column"
            >
                <View>
                    <View
                        paddingHorizontal="$5"
                        $sm={{ paddingHorizontal: '$6' }}
                    >
                        <SectionHead
                            title={isNew ? 'New Client' : getClientNames(purchaser.buyers)}
                            titleChip={!isNew && <PurchaserStatusChip />}
                            back={{ label: 'Clients', onPress: () => navigate(`/${orgId}/clients`) }}
                            buttons={
                                <XStack
                                    gap="$2"
                                    alignItems="center"
                                >
                                    {!formFullyDisabled && (
                                        <>
                                            {editMode ? (
                                                <>
                                                    <Group orientation="horizontal">
                                                        <Group.Item>
                                                            <Button
                                                                key="save"
                                                                theme="primary"
                                                                icon={<SaveRounded />}
                                                                data-help-id="client-save-button"
                                                                onPress={async () => {
                                                                    if (savingRef.current) return
                                                                    savingRef.current = true
                                                                    setSaving(true)
                                                                    const pass =
                                                                        await formRef.current?.saveForm()
                                                                    if (pass && !isNew) {
                                                                        setEditMode(false)
                                                                    }
                                                                    setSaving(false)
                                                                    savingRef.current = false
                                                                }}
                                                            >
                                                                Save
                                                            </Button>
                                                        </Group.Item>
                                                        <Group.Item>
                                                            <Button
                                                                key="cancel"
                                                                variant="outlined"
                                                                disabled={saving}
                                                                onPress={() => {
                                                                    if (isNew) {
                                                                        navigate(`/${orgId}/clients`, {
                                                                            replace: true,
                                                                        })
                                                                    }
                                                                    formRef.current?.resetForm()
                                                                    setEditMode(false)
                                                                }}
                                                            >
                                                                Cancel
                                                            </Button>
                                                        </Group.Item>
                                                    </Group>
                                                    {user.permissions.includes('purchasers.delete') && (
                                                        <Menu
                                                            open={menuOpen}
                                                            onOpenChange={setMenuOpen}
                                                        >
                                                            <Menu.Trigger asChild>
                                                                <Button
                                                                    ref={anchorRef}
                                                                    onPress={handleMenuToggle}
                                                                    variant="outlined"
                                                                >
                                                                    <MoreVertRounded size={18} />
                                                                </Button>
                                                            </Menu.Trigger>
                                                            <Menu.Portal>
                                                                <Menu.Content>
                                                                    <Menu.Item
                                                                        key="delete"
                                                                        onPress={() => {
                                                                            handleMenuClose()
                                                                            setShowDeletePurchaser(true)
                                                                        }}
                                                                    >
                                                                        <Menu.ItemIcon>
                                                                            <DeleteRounded
                                                                                size={18}
                                                                                color="$red10"
                                                                            />
                                                                        </Menu.ItemIcon>
                                                                        <Menu.ItemTitle color="$red10">
                                                                            Delete Client
                                                                        </Menu.ItemTitle>
                                                                    </Menu.Item>
                                                                </Menu.Content>
                                                            </Menu.Portal>
                                                        </Menu>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    <Button
                                                        onPress={() => setEditMode(true)}
                                                        theme="primary"
                                                        icon={<EditRounded />}
                                                        data-help-id="client-edit-button"
                                                        data-tour="client-edit-button"
                                                    >
                                                        Edit
                                                    </Button>
                                                    {user.permissions.includes('purchasers.delete') && (
                                                        <Menu
                                                            open={menuOpen}
                                                            onOpenChange={setMenuOpen}
                                                        >
                                                            <Menu.Trigger asChild>
                                                                <Button
                                                                    ref={anchorRef}
                                                                    onPress={handleMenuToggle}
                                                                    variant="outlined"
                                                                >
                                                                    <MoreVertRounded size={18} />
                                                                </Button>
                                                            </Menu.Trigger>
                                                            <Menu.Portal>
                                                                <Menu.Content>
                                                                    <Menu.Item
                                                                        key="delete"
                                                                        onPress={() => {
                                                                            handleMenuClose()
                                                                            setShowDeletePurchaser(true)
                                                                        }}
                                                                    >
                                                                        <Menu.ItemIcon>
                                                                            <DeleteRounded
                                                                                size={18}
                                                                                color="$red10"
                                                                            />
                                                                        </Menu.ItemIcon>
                                                                        <Menu.ItemTitle color="$red10">
                                                                            Delete Client
                                                                        </Menu.ItemTitle>
                                                                    </Menu.Item>
                                                                </Menu.Content>
                                                            </Menu.Portal>
                                                        </Menu>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}
                                </XStack>
                            }
                        />
                    </View>
                    <View
                        paddingHorizontal="$5"
                        $sm={{ paddingHorizontal: '$6' }}
                    >
                        <ScrollableTabs
                            value={
                                tabs[
                                    tabs.findIndex(
                                        ([label]) => label.toLowerCase().replaceAll(' ', '-') == activeTab,
                                    )
                                ]?.[0] ?? tabs[0][0]
                            }
                            onChange={val => {
                                if (val != undefined) {
                                    const tabLabel = val as string
                                    navigate(`./${tabLabel.toLowerCase().replaceAll(' ', '-')}`, {
                                        replace: true,
                                        relative: 'path',
                                    })
                                }
                            }}
                        >
                            <ScrollableTabList ref={tabListRef}>
                                {tabs
                                    .filter((_, i) => !isNew || i <= 1)
                                    .map(([label], i) => {
                                        const isActive =
                                            label.toLowerCase().replaceAll(' ', '-') === activeTab
                                        return (
                                            <Tabs.Tab
                                                key={label}
                                                value={label}
                                                backgroundColor="transparent"
                                                borderBottomWidth={2}
                                                borderBottomColor={isActive ? '$primary' : 'transparent'}
                                                borderRadius={0}
                                                marginBottom={-1}
                                                paddingVertical={10}
                                                paddingHorizontal={14}
                                                height="auto"
                                                hoverStyle={{ backgroundColor: 'transparent' }}
                                                pressStyle={{ backgroundColor: 'transparent', opacity: 0.8 }}
                                                animation="quick"
                                                {...({
                                                    Client: 'client-details-tab',
                                                    Wishlist: 'client-wishlist-tab',
                                                    'Purchased Property': 'client-purchased-tab',
                                                    Files: 'client-files-tab',
                                                    'Audit Log': 'client-audit-tab',
                                                }[label]
                                                    ? {
                                                          'data-help-id': {
                                                              Client: 'client-details-tab',
                                                              Wishlist: 'client-wishlist-tab',
                                                              'Purchased Property': 'client-purchased-tab',
                                                              Files: 'client-files-tab',
                                                              'Audit Log': 'client-audit-tab',
                                                          }[label],
                                                      }
                                                    : {})}
                                                {...(label === 'Client'
                                                    ? { 'data-tour': 'purchaser-tab-client' }
                                                    : label === 'Wishlist'
                                                      ? { 'data-tour': 'purchaser-tab-wishlist' }
                                                      : {})}
                                            >
                                                <XStack
                                                    alignItems="center"
                                                    gap="$2"
                                                >
                                                    <SizableText
                                                        size="$2"
                                                        fontWeight="500"
                                                        color={isActive ? '$color' : '$colorMuted'}
                                                    >
                                                        {label}
                                                    </SizableText>
                                                    {tabCount[label] != null && (
                                                        <View
                                                            paddingHorizontal={4}
                                                            paddingVertical={0.5}
                                                            borderRadius={999}
                                                            backgroundColor={
                                                                isActive ? '$accentSoft' : '$backgroundRaised'
                                                            }
                                                        >
                                                            {/* fontSize/lineHeight via `style`: SizableText's
                                                                fontFamily variant re-derives them from the size
                                                                token and clobbers the equivalent props. */}
                                                            <SizableText
                                                                fontFamily="$mono"
                                                                color={
                                                                    isActive ? '$accentText' : '$colorMuted'
                                                                }
                                                                style={{
                                                                    fontSize: 10,
                                                                    lineHeight: '14px',
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {tabCount[label]}
                                                            </SizableText>
                                                        </View>
                                                    )}
                                                </XStack>
                                            </Tabs.Tab>
                                        )
                                    })}
                            </ScrollableTabList>
                        </ScrollableTabs>
                    </View>
                    {/*<View $sm={{ display: 'none' }}>
                        <Select
                            editMode
                            value={activeTab}
                            onChange={(e: any, val: any) => {
                                if (val) {
                                    navigate(`./${val}`, {
                                        replace: true,
                                        relative: 'path',
                                    })
                                }
                            }}
                        >
                            {tabs
                                .filter(([val], i) => !isNew || i <= 1)
                                .map(([label], i) => (
                                    <Option
                                        key={label}
                                        value={label.toLowerCase().replaceAll(' ', '-')}
                                    >
                                        {label}
                                    </Option>
                                ))}
                        </Select>
                        <Separator marginTop="$4" />
                    </View>*/}
                </View>
                <View
                    paddingHorizontal="$5"
                    $sm={{ paddingHorizontal: '$6' }}
                    marginTop="$6"
                    flex={1}
                    overflow="auto"
                >
                    <Routes>
                        {(isNew ? ([tabs[0], tabs[1]] as any) : tabs).map(([label, Component], i) => (
                            <Route
                                key={label}
                                path={label.toLowerCase().replaceAll(' ', '-')}
                                element={
                                    label === 'Client' ||
                                    label === 'Purchased Property' ||
                                    label === 'Wishlist' ? (
                                        <Component ref={formRef} />
                                    ) : (
                                        <Component />
                                    )
                                }
                            />
                        ))}
                        <Route
                            index
                            element={
                                <Navigate
                                    to={tabs[0][0].toLowerCase().replaceAll(' ', '-')}
                                    replace={true}
                                />
                            }
                        />
                    </Routes>
                </View>
            </View>
            <Dialog
                open={showDeletePurchaser}
                onOpenChange={open => {
                    if (!open) setShowDeletePurchaser(false)
                }}
            >
                <Dialog.Portal>
                    <Dialog.Overlay
                        key="overlay"
                        animation="quick"
                        opacity={0.5}
                        enterStyle={{ opacity: 0 }}
                        exitStyle={{ opacity: 0 }}
                    />
                    <Dialog.Content
                        key="content"
                        bordered
                        elevate
                        padding="$4"
                        borderRadius="$4"
                        gap="$4"
                    >
                        <Dialog.Title>Delete Client</Dialog.Title>
                        <Dialog.Description>
                            <Text>Are you sure you want to delete this client?</Text>
                        </Dialog.Description>
                        <XStack
                            gap="$2"
                            justifyContent="flex-end"
                        >
                            <Button
                                theme="red"
                                disabled={deletePurchaserMutation.isPending}
                                onPress={async () => {
                                    if (!purchaserId) return
                                    await deletePurchaserMutation.mutateAsync({ purchaserId })
                                    navigate(`../`, { replace: true })
                                }}
                                icon={<DeleteRounded />}
                            >
                                Delete
                            </Button>
                            <Button
                                variant="outlined"
                                onPress={() => setShowDeletePurchaser(false)}
                            >
                                Cancel
                            </Button>
                        </XStack>
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog>
        </>
    )
}

export default () => {
    const trpc = useTRPC()
    const { user } = useUserData()
    const usersApi = useUsersApi()
    const { purchaserId } = useParams<{ purchaserId: string }>()

    const isNew = purchaserId == 'new'

    // Use tRPC to fetch purchaser
    const purchaserQuery = useQuery({
        ...trpc.purchaser.getPurchaser.queryOptions({ purchaserId: purchaserId! }),
        enabled: !!purchaserId && !isNew,
    })

    const [purchaser, setPurchaser] = useState<InternalPurchaserSchema | undefined>(undefined)

    useEffect(() => {
        if (isNew) {
            if (user.salesAssistantConjunctionalAgencyId) {
                // Wait for users data to be available
                if (usersApi.data) {
                    const conjUser = usersApi.data
                        .filter(x => x.userId != user.userId)
                        .find(x => x.conjunctionalAgencyId == user.salesAssistantConjunctionalAgencyId)

                    setPurchaser({
                        purchaserStatus: 'consultation',
                        agents: [
                            {
                                userId: conjUser?.userId ?? user.userId,
                                split: 1,
                            },
                        ],
                    } as any)
                }
            } else {
                setPurchaser({
                    purchaserStatus: 'consultation',
                    agents: [
                        {
                            userId: user.salesAssistantLeadAgentId ?? user.userId,
                            split: 1,
                        },
                    ],
                } as any)
            }
        } else if (purchaserQuery.data) {
            setPurchaser(purchaserQuery.data)
        }
    }, [purchaserId, purchaserQuery.data, usersApi.data])

    const refreshPurchaser = () => purchaserQuery.refetch().then(() => {})

    if (!purchaser) {
        return <Loading />
    }

    return (
        <PurchaserContext.Provider value={{ purchaser, setPurchaser: setPurchaser as any, refreshPurchaser }}>
            <EditModeProvider defaultEdit={isNew}>
                <Inner />
            </EditModeProvider>
        </PurchaserContext.Provider>
    )
}
